import { useState } from 'react';
import { Alert, Button, Checkbox, Group, Loader, Stack, Switch, Tabs } from '@mantine/core';
import { IconAlertTriangle, IconDownload } from '@tabler/icons-react';
import { UsageMetricKey } from './aggregate';
import { useUsageMetrics } from './hooks/useUsageMetrics';
import { useUsageMetricsExport } from './hooks/useUsageMetricsExport';
import TotalsPanel from './TotalsPanel';
import PerTenantPanel from './PerTenantPanel';
import SummaryPanel from './SummaryPanel';

export default function UsageMetricsTab() {
  const [includeInternal, setIncludeInternal] = useState(false);
  const [includeMessages, setIncludeMessages] = useState(false);
  const [includeConversations, setIncludeConversations] = useState(false);
  const { rows, isLoading, isError } = useUsageMetrics(includeInternal);
  const { downloadExport, isLoading: isExporting } = useUsageMetricsExport();

  const extraTotalKeys: UsageMetricKey[] = [
    ...(includeMessages ? (['messages'] as const) : []),
    ...(includeConversations ? (['conversations'] as const) : []),
  ];

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" wrap="wrap">
        <Group gap="lg" wrap="wrap">
          <Switch
            label="Include internal companies"
            checked={includeInternal}
            onChange={event => setIncludeInternal(event.currentTarget.checked)}
          />
          <Checkbox
            label="Include Siesta messages in totals"
            checked={includeMessages}
            onChange={event => setIncludeMessages(event.currentTarget.checked)}
          />
          <Checkbox
            label="Include conversations in totals"
            checked={includeConversations}
            onChange={event => setIncludeConversations(event.currentTarget.checked)}
          />
        </Group>
        <Button
          leftSection={<IconDownload size={16} />}
          variant="light"
          loading={isExporting}
          onClick={() => downloadExport({ includeInternal, includeMessages, includeConversations })}
        >
          Download xlsx
        </Button>
      </Group>

      {isLoading && (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      )}

      {isError && (
        <Alert icon={<IconAlertTriangle />} color="red" variant="light">
          Could not load usage metrics. Try refreshing the page.
        </Alert>
      )}

      {!isLoading && !isError && (
        <Tabs defaultValue="totals" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="totals">Totals</Tabs.Tab>
            <Tabs.Tab value="per-tenant">Per tenant</Tabs.Tab>
            <Tabs.Tab value="summary">Summary</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="totals" pt="md">
            <TotalsPanel rows={rows ?? []} extraTotalKeys={extraTotalKeys} />
          </Tabs.Panel>
          <Tabs.Panel value="per-tenant" pt="md">
            <PerTenantPanel rows={rows ?? []} extraTotalKeys={extraTotalKeys} />
          </Tabs.Panel>
          <Tabs.Panel value="summary" pt="md">
            <SummaryPanel rows={rows ?? []} extraTotalKeys={extraTotalKeys} />
          </Tabs.Panel>
        </Tabs>
      )}
    </Stack>
  );
}
